
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProductVariant } from "@/types/product";

interface EnhancedProductVariantSelectorProps {
  variants: ProductVariant[];
  selectedVariant: ProductVariant | null;
  onVariantChange: (variant: ProductVariant) => void;
  className?: string;
}

const EnhancedProductVariantSelector = ({
  variants,
  selectedVariant,
  onVariantChange,
  className = ""
}: EnhancedProductVariantSelectorProps) => {
  const [hoveredVariant, setHoveredVariant] = useState<ProductVariant | null>(null);

  // Group variants by option type (color, size, etc.)
  const groupedOptions = variants.reduce((acc, variant) => {
    Object.entries(variant.variant_options || {}).forEach(([key, value]) => {
      if (!acc[key]) acc[key] = new Set();
      acc[key].add(value);
    });
    return acc;
  }, {} as Record<string, Set<string>>);

  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});

  // Check if variants have meaningful options
  const hasVariantOptions = Object.keys(groupedOptions).length > 0;

  const handleOptionChange = (optionType: string, optionValue: string) => {
    const newOptions = { ...selectedOptions, [optionType]: optionValue };
    setSelectedOptions(newOptions);

    // Find variant that matches all selected options
    const matchingVariant = variants.find(variant => {
      return Object.entries(newOptions).every(([key, value]) => 
        variant.variant_options?.[key] === value
      );
    });

    if (matchingVariant) {
      onVariantChange(matchingVariant);
    }
  };

  // Handle direct variant selection (for variants without meaningful options)
  const handleDirectVariantChange = (variantId: string) => {
    const variant = variants.find(v => v.id === variantId);
    if (variant) {
      onVariantChange(variant);
    }
  };

  if (variants.length <= 1) return null;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Render option-based selector if variants have meaningful options */}
      {hasVariantOptions && Object.entries(groupedOptions).map(([optionType, values]) => (
        <div key={optionType} className="space-y-3">
          <h4 className="text-sm font-medium text-foreground capitalize">
            {optionType}: {selectedOptions[optionType] && (
              <span className="font-normal text-muted-foreground">
                {selectedOptions[optionType]}
              </span>
            )}
          </h4>
          <div className="flex flex-wrap gap-2">
            {Array.from(values).map((value) => {
              const isSelected = selectedOptions[optionType] === value;
              const isAvailable = variants.some(v => 
                v.variant_options?.[optionType] === value && v.stock_quantity > 0
              );

              return (
                <Button
                  key={value}
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  disabled={!isAvailable}
                  onClick={() => handleOptionChange(optionType, value)}
                  onMouseEnter={() => {
                    const variant = variants.find(v => v.variant_options?.[optionType] === value);
                    setHoveredVariant(variant || null);
                  }}
                  onMouseLeave={() => setHoveredVariant(null)}
                  className={`
                    relative transition-all duration-200
                    ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
                    ${!isAvailable ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
                  `}
                >
                  {value}
                  {!isAvailable && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      Out of Stock
                    </Badge>
                  )}
                </Button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Render simple variant selector if no meaningful options */}
      {!hasVariantOptions && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-foreground">
            Choose Option:
          </h4>
          <Select
            value={selectedVariant?.id || ""}
            onValueChange={handleDirectVariantChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a variant" />
            </SelectTrigger>
            <SelectContent>
              {variants.map((variant) => (
                <SelectItem 
                  key={variant.id} 
                  value={variant.id}
                  disabled={variant.stock_quantity === 0}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="flex-1">
                      {variant.name}
                      {variant.stock_quantity === 0 && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          Out of Stock
                        </Badge>
                      )}
                    </span>
                    {variant.price && (
                      <span className="text-sm font-medium ml-2">
                        ${variant.price.toFixed(2)}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Selection Prompt */}
      {!selectedVariant && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-800">
            <span className="text-sm font-medium">
              {hasVariantOptions 
                ? "Please select all options to view details and pricing"
                : "Please select a variant to view details and pricing"
              }
            </span>
          </div>
        </div>
      )}

      {/* Display variant-specific information */}
      {(selectedVariant || hoveredVariant) && (
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {(hoveredVariant || selectedVariant)?.name}
            </span>
            {(hoveredVariant || selectedVariant)?.sku && (
              <Badge variant="outline" className="text-xs">
                SKU: {(hoveredVariant || selectedVariant)?.sku}
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted-foreground">
              Stock: {(hoveredVariant || selectedVariant)?.stock_quantity} available
            </span>
            {(hoveredVariant || selectedVariant)?.weight && (
              <span className="text-muted-foreground">
                Weight: {(hoveredVariant || selectedVariant)?.weight}kg
              </span>
            )}
          </div>

          {(hoveredVariant || selectedVariant)?.price && (
            <div className="text-lg font-semibold text-primary">
              ${(hoveredVariant || selectedVariant)?.price?.toFixed(2)}
              {(hoveredVariant || selectedVariant)?.compare_at_price && (
                <span className="text-sm text-muted-foreground line-through ml-2">
                  ${(hoveredVariant || selectedVariant)?.compare_at_price?.toFixed(2)}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedProductVariantSelector;
